import { SliceCreator } from '../TrainingState';
import {
  SaveAnswersMutation,
  SaveAnswersMutationVariables,
  SaveAnswersDocument
} from '~/data';
import { client } from '~/data/client';
import { Sequence } from '../navigation/NavigationSlice';
import { isInTestedOutChapter } from '../navigation/requiredPath';

export interface QuestionAnswersSlice {
  saveQuestionAnswers: (
    questionFieldId: string,
    chapterSequenceId: string
  ) => void;
}

/**
 * Save question answers on the back-end
 * @param variables The GraphQL query variables
 * @returns The values that have been updated
 */
export async function saveQuestionAnswers(variables: {
  learnerTrainingUuid: string;
  chapterSequenceId: string;
  questionFieldId: string;
}) {
  const result = await client.mutate<
    SaveAnswersMutation,
    SaveAnswersMutationVariables
  >({
    mutation: SaveAnswersDocument,
    variables
  });

  if (result.errors?.length || result.data?.saveAnswers?.errors.length) {
    throw new Error('Error saving settings...');
  }

  return variables;
}

export const createSaveQuestionAnswersRequest: SliceCreator<
  QuestionAnswersSlice
> = (set, get) => {
  /**
   * @param newValues contain data about question
   */
  async function storeQuestionAnswers(
    newValues: Partial<SaveAnswersMutationVariables>
  ) {
    const { learnerTrainingId, chapterSequenceId } = get();

    return await saveQuestionAnswers({
      learnerTrainingUuid: learnerTrainingId,
      chapterSequenceId: chapterSequenceId,
      questionFieldId: '',
      ...newValues
    });
  }

  return {
    saveQuestionAnswers: (
      questionFieldId: string,
      chapterSequenceId: string
    ) => {
      const { items } = get();
      const sequence = items.find(
        (item): item is Sequence =>
          item.kind === 'sequence' &&
          item.chapterSequenceId === chapterSequenceId
      );
      if (!sequence) return;

      // A chapter the learner tested out of is open for review only: their
      // pretest answers stand in for its quiz, so nothing here is saved.
      if (isInTestedOutChapter(items, sequence)) return;

      storeQuestionAnswers({
        questionFieldId,
        chapterSequenceId
      }).then();
    }
  };
};
